import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import Footer from "../components/Footer";
import { X, InfoIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ComparisonTable from "../components/ComparisonTable";
import { useLanguage } from "../context/LanguageContext";
import MobileSignupStep from "../components/mobile-flow/MobileSignupStep";
import MobileProfileStep from "../components/mobile-flow/MobileProfileStep";
import MobileSubscriptionStep from "../components/mobile-flow/MobileSubscriptionStep";
import MobileConfirmationStep from "../components/mobile-flow/MobileConfirmationStep";
import InterviewPrepInterestForm from "../components/InterviewPrepInterestForm";
import { useTranslation } from "react-i18next";
import "../styles/LandingStyles.css";
import LandingInterviewMobile from "./LandingInterviewMobile";

const Card = ({ icon, title, description, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 100 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      className="flex h-full flex-col items-center rounded-[48px] border border-[#14B82C] bg-[#F0FDF1] p-6 pt-10 text-center"
    >
      {icon && <img src={icon} alt="card-icon" className="mb-4 h-16 w-16" />}
      <div className="mb-2 flex items-center justify-center gap-2">
        <h3 className="text-[32px] font-bold text-[#042F0C]">{title}</h3>
      </div>
      <p className="w-full min-w-80 text-xl font-normal leading-[30px] text-[#3d3d3d]">
        {description}
      </p>
    </motion.div>
  );
};

const features = [
  "feature1",
  "feature2",
  "feature3",
  "feature4",
  "feature5",
  "feature6",
  "feature7",
  "feature8",
];

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);
  return isMobile;
}

const LandingInterviewXcelerator = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [mobileModalStep, setMobileModalStep] = useState(null); // null | 'signup' | 'profile' | 'subscription' | 'confirmation'
  const { currentLanguage, changeLanguage } = useLanguage();
  const handleLanguageChange = (lang) => {
    changeLanguage(lang);
    localStorage.setItem("i18nextLng", lang);
    document.documentElement.lang = lang;
  };
  const [showBanner, setShowBanner] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [showInterestForm, setShowInterestForm] = useState(false);
  const { t, i18n } = useTranslation();

  // Get current month name based on language
  const getCurrentMonthName = () => {
    const monthNames = {
      en: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
      es: [
        "enero",
        "febrero",
        "marzo",
        "abril",
        "mayo",
        "junio",
        "julio",
        "agosto",
        "septiembre",
        "octubre",
        "noviembre",
        "diciembre",
      ],
    };
    const lang = i18n.language.split("-")[0] || "en";
    const currentMonthIndex = new Date().getMonth();
    return (
      monthNames[lang]?.[currentMonthIndex] || monthNames.en[currentMonthIndex]
    );
  };

  const currentMonth = getCurrentMonthName();

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPopup(true);
    }, 10000); // 10 seconds
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (showPopup) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showPopup]);

  const handleEnrollClick = () => {
    setShowInterestForm(true);
  };

  // Handle mobile modal flow completion
  const handleMobileFlowComplete = () => {
    localStorage.removeItem("inMobileModalFlow");
    navigate("/learn");
  };

  // Handle mobile modal flow close
  const handleMobileFlowClose = () => {
    localStorage.removeItem("inMobileModalFlow");
    setMobileModalStep(null);
  };

  // Render mobile landing page if on mobile
  if (isMobile) {
    return <LandingInterviewMobile />;
  }

  return (
    <>
      {/* Orange Banner */}
      {showBanner && (
        <div className="relative z-30 w-full bg-[#FFBF00] py-3 text-center text-lg font-medium text-[#454545]">
          <span>
            {t("interviewAccelerator.banner").replace(/October|octubre/i, currentMonth)}
          </span>
          <button
            onClick={() => setShowBanner(false)}
            className="absolute right-6 top-1/2 flex -translate-y-1/2 items-center justify-center rounded-full p-2 text-[#3D3D3D]"
            aria-label="Close"
            type="button"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
      )}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{ zIndex: 100, position: "relative" }}
      >
        <Navbar user={user} onGetStartedClick={handleEnrollClick} />
      </motion.div>

      {/* section 1 */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="section1-background-custom mb-[157px] mt-[-86px] flex w-full flex-col items-center justify-center pt-[186px] lg:px-0"
      >
        <div className="wrapper">
          <div className="mx-auto flex w-full flex-col items-center justify-center">
            <h1 className="mb-[25px] w-full text-center text-[64px] font-extrabold leading-tight text-black">
              {t("interviewAccelerator.hero.title1")}
              <br />
              {t("interviewAccelerator.hero.title2")}
            </h1>
            <h1 className="mb-12 w-full max-w-[1000px] whitespace-pre-line text-center text-2xl font-medium leading-relaxed text-[#042F0C]">
              {t("interviewAccelerator.hero.subtitle")}
            </h1>
            <div className="mx-auto mb-[26px] flex w-full flex-col items-center justify-center gap-3 text-base font-medium text-black sm:w-auto sm:flex-row sm:gap-[24px]">
              <button
                className="flex h-[44px] w-[193px] items-center justify-center rounded-full border border-black bg-white px-0 text-black transition hover:bg-gray-100"
                onClick={() =>
                  window.open(
                    "https://calendly.com/bammbuu-languages/career-strategy-english-audit",
                    "_blank",
                  )
                }
              >
                {t("interviewAccelerator.hero.freeConsultation")}
              </button>
              <button
                onClick={handleEnrollClick}
                className="flex h-[44px] w-[174px] items-center justify-center rounded-full border border-black bg-[#FFBF00] px-0 text-base font-medium text-black transition hover:bg-[#ffd94d]"
              >
                {t("interviewAccelerator.hero.interested")}
              </button>
            </div>
          </div>
          <div className="mx-auto flex w-full flex-col items-center">
            <div className="relative m-0 w-full overflow-hidden rounded-[64px] bg-[#eaeaea] p-0 sm:rounded-[3rem]">
              <iframe
                width="100%"
                height="100%"
                style={{
                  aspectRatio: "16/9",
                  display: "block",
                  minHeight: "720px",
                }}
                src="https://www.youtube.com/embed/wK5caTEtp60"
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
            <div className="mt-6 w-full text-center">
              <span className="text-sm font-normal italic text-[#5D5D5D] sm:text-base">
                {t("interviewAccelerator.hero.videoCaption")}
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="overflow-hidden px-20">
        {/* section 2 */}
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mx-auto mb-20 flex w-full flex-col items-center justify-center"
        >
          <div className="mx-auto my-0 flex w-full max-w-[848px] flex-col items-center justify-center gap-4">
            <h1 className="text-center !text-[56px] font-semibold text-black sm:text-5xl">
              {t("interviewAccelerator.why.title")}
              <br />
              {t("interviewAccelerator.why.title2")}
            </h1>
            <p className="text-center text-2xl font-normal text-[#3d3d3d] sm:text-lg">
              {t("interviewAccelerator.why.description")}
             <b> {t("interviewAccelerator.why.description2")}</b>
             {t("interviewAccelerator.why.description3")}
             <b> {t("interviewAccelerator.why.description4")}</b>
             {t("interviewAccelerator.why.description5")}
            </p>
          </div>
        </motion.div>

        {/* section 3 - Cards */}
        <motion.div
          initial={{ opacity: 0, y: 80 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mx-auto mb-[200px] flex w-full flex-col items-center justify-center"
        >
          {/* First row: 3 cards */}
          <div className="grid w-full grid-cols-1 items-stretch gap-10 sm:grid-cols-2 md:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="h-full w-full"
              >
                <Card
                  index={i}
                  icon={
                    i === 0
                      ? "/svgs/conversation-first.svg"
                      : i === 1
                        ? "/svgs/personalized-plans.svg"
                        : "/svgs/certified-experts.svg"
                  }
                  title={t(`interviewAccelerator.cards.card${i + 1}.title`)}
                  description={t(`interviewAccelerator.cards.card${i + 1}.desc`)}
                />
              </motion.div>
            ))}
          </div>
          {/* Second row: 2 cards */}
          <div className="mt-10 grid w-full grid-cols-1 items-stretch gap-10 sm:grid-cols-2">
            {[3, 4].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: (i - 3) * 0.1 }}
                className="h-full w-full"
              >
                <Card
                  index={i}
                  icon={
                    i === 3
                      ? "/svgs/all-one.svg"
                      : "/svgs/confidence-guarantee.svg"
                  }
                  title={t(`interviewAccelerator.cards.card${i + 1}.title`)}
                  description={t(`interviewAccelerator.cards.card${i + 1}.desc`)}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* section 4 - Two Column Layout: Features + Package */}
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mx-auto mb-[200px] flex w-full flex-col items-center justify-center"
        >
          <div className="mx-auto w-full max-w-8xl">
            <h1 className="mb-20 text-center text-[56px] font-semibold text-black px-10">
              {t("interviewAccelerator.whatYouGet.title")}
            </h1>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
              {/* Left Column - Features List */}
              <div className="mt-16 space-y-4">
                {features.map((featureKey, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <img
                      src="/svgs/tick-star.svg"
                      alt="Check"
                      className="mr-1 h-8 w-8"
                    />
                    <span className="text-2xl font-medium leading-[30px] text-[#042F0C]">
                      {t(`interviewAccelerator.features.${featureKey}`)}
                    </span>
                  </div>
                ))}

                {/* Money-back guarantee disclaimer */}
                <div className="mt-8 flex h-20 items-start gap-2 rounded-xl bg-[#FFFFEA] p-2">
                  <InfoIcon className="h-4 w-4 shrink-0 text-[#FFBF00]" />
                  <span className="text-xl font-light italic text-[#3d3d3d]">
                    {t("interviewAccelerator.moneyBackDisclaimer")}
                  </span>
                </div>
              </div>

              {/* Right Column - Package Card */}
              <div className="flex justify-center lg:justify-end">
                <div className="w-full max-w-2xl overflow-hidden rounded-[48px] border border-[#14B82C] bg-[#E6FDE9] shadow-lg">
                  <div className="bg-[#FFBF00] py-3 text-center">
                    <span className="text-base font-semibold leading-5">
                      {t("interviewAccelerator.pricing.header")}
                    </span>
                  </div>
                  <div className="flex h-full min-h-[400px] flex-col items-center justify-between px-4 py-12">
                    <div className="flex w-full flex-col items-center">
                      <div className="mb-1 whitespace-pre-line text-center text-[56px] font-semibold leading-tight tracking-tight text-black !px-20">
                        {t("interviewAccelerator.pricing.priceTitle")}
                      </div>
                      <div className="mb-2 text-center text-lg font-normal leading-6 text-black">
                        {t("interviewAccelerator.pricing.includes")}
                      </div>
                      <div className="relative mx-auto my-5 w-[80%] overflow-hidden rounded-2xl bg-[#eaeaea]">
                        <iframe
                          width="100%"
                          height="100%"
                          style={{
                            aspectRatio: "16/9",
                            display: "block",
                          }}
                          src="https://www.youtube.com/embed/KRdM6uLQRaA"
                          title="YouTube video player"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        ></iframe>
                      </div>
                      <div className="flex flex-col items-center justify-center space-y-2">
                        <InfoIcon className="h-6 w-6 text-[#FFBF00]" />
                        <p className="text-center text-base font-normal text-[#3d3d3d]">
                          {t("interviewAccelerator.pricing.enrollDescription")}
                        </p>
                      </div>
                    </div>

                    <div className="mt-auto w-full space-y-3 pb-6">
                      <button
                        className="h-[48px] w-full rounded-[24px] border border-[#042F0C] bg-[#14B82C] py-2 text-lg font-semibold text-black"
                        onClick={handleEnrollClick}
                      >
                        {t("interviewAccelerator.hero.interested")}
                      </button>
                      <button
                        className="h-[48px] w-full rounded-[24px] border border-[#042F0C] bg-white py-2 text-lg font-semibold text-[#12551E]"
                        onClick={() =>
                          window.open(
                            "https://calendly.com/bammbuu-languages/career-strategy-english-audit",
                            "_blank",
                          )
                        }
                      >
                        {t("interviewAccelerator.hero.freeConsultation")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Comparison Table Section */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <ComparisonTable onEnrollClick={handleEnrollClick} translationPrefix="interviewAccelerator" />
        </motion.div>
      </div>
      {/* Mobile Modal Stepper Flow */}
      {isMobile && mobileModalStep === "signup" && (
        <MobileSignupStep
          onNext={() => setMobileModalStep("profile")}
          onClose={handleMobileFlowClose}
        />
      )}
      {isMobile && mobileModalStep === "profile" && (
        <MobileProfileStep
          onNext={() => setMobileModalStep("subscription")}
          onBack={() => setMobileModalStep("signup")}
          onClose={handleMobileFlowClose}
        />
      )}
      {isMobile && mobileModalStep === "subscription" && (
        <MobileSubscriptionStep
          onNext={() => setMobileModalStep("confirmation")}
          onBack={() => setMobileModalStep("profile")}
          onClose={handleMobileFlowClose}
        />
      )}
      {isMobile && mobileModalStep === "confirmation" && (
        <MobileConfirmationStep onClose={handleMobileFlowComplete} />
      )}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <Footer />
      </motion.div>
      {/* Interview Prep Interest Form */}
      <InterviewPrepInterestForm
        isOpen={showInterestForm}
        onClose={() => setShowInterestForm(false)}
      />

      {/* Popup Modal */}
      {showPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="relative flex h-[490px] w-[1080px] max-w-full flex-col items-center overflow-hidden rounded-3xl bg-white p-0 shadow-2xl">
            {/* Background image */}
            <img
              src="/images/banner-background.png"
              alt="Banner Background"
              className="absolute inset-0 h-full w-full object-cover opacity-80"
            />

            <div className="relative z-10 flex h-full w-full flex-col items-center justify-center p-8 sm:p-12">
              <button
                onClick={() => setShowPopup(false)}
                className="absolute right-6 top-6 flex items-center justify-center rounded-full bg-[#E7E7E7] bg-opacity-70 p-2 text-[#3D3D3D] hover:bg-gray-100"
                aria-label="Close"
                type="button"
              >
                <X className="h-6 w-6" />
              </button>
              <img
                src="/svgs/exam-prep-modal-icon.svg"
                alt={t("interviewAccelerator.modal.bannerIconAlt")}
                className="mx-auto mb-6"
              />
              <h2 className="mb-3 text-center text-[32px] font-bold text-black">
                {t("interviewAccelerator.modal.title")}
              </h2>
              <p className="mb-8 max-w-xl text-center text-xl font-medium text-[#454545]">
                {t("interviewAccelerator.modal.spotsFilling").replace(
                  /October|octubre/i,
                  currentMonth,
                )}
              </p>
              <button
                className="rounded-full border border-[#042F0C] bg-[#14B82C] px-8 py-2 text-base font-medium text-black transition hover:bg-green-700"
                onClick={() => {
                  setShowPopup(false);
                  handleEnrollClick();
                }}
              >
                {t("interviewAccelerator.hero.interested")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LandingInterviewXcelerator;
